
import { useState, useEffect } from "react";
import { Layout } from "@/components/Layout";
import { AdminLayout } from "@/components/AdminLayout";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { UserAvatar } from "@/components/UserAvatar";
import { Package, Plus, Trash2, Loader2, Upload, Edit, Zap, Filter } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

interface ShopItem {
    id: string;
    name: string;
    description: string;
    type: 'boost' | 'border';
    price_eur: number;
    metadata: any;
    is_active: boolean;
    is_leveling: boolean;
}

const AdminProducts = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState<ShopItem[]>([]);
    const [uploading, setUploading] = useState(false);
    const [editingItem, setEditingItem] = useState<ShopItem | null>(null);

    // Form state
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [price, setPrice] = useState("");
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [isLeveling, setIsLeveling] = useState(false);
    const [styleSlug, setStyleSlug] = useState("");
    const [itemType, setItemType] = useState<'border' | 'boost'>('border');

    // Filter state
    type FilterType = 'all' | 'borders' | 'boosts' | 'leveling' | 'not_leveling';
    const [filter, setFilter] = useState<FilterType>('all');

    useEffect(() => {
        checkAdmin();
        fetchItems();
    }, [user]);

    const checkAdmin = async () => {
        if (!user) return;
        const { data } = await supabase
            .from('profiles')
            .select('is_admin')
            .eq('id', user.id)
            .single();

        if (!data?.is_admin) {
            navigate('/');
            toast.error("Unauthorized access");
        }
    };

    const fetchItems = async () => {
        try {
            const { data, error } = await supabase
                .from('shop_items')
                .select('*')
                .order('created_at', { ascending: false });

            if (error) throw error;
            setItems(data as ShopItem[]);
        } catch (error) {
            console.error('Error fetching items:', error);
            toast.error("Failed to fetch products");
        } finally {
            setLoading(false);
        }
    };

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setImageFile(file);
            setPreviewUrl(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (editingItem) {
            await handleUpdateItem();
        } else {
            await handleAddItem();
        }
    };

    const handleAddItem = async () => {
        if (!imageFile || !name || price === "") {
            toast.error("Please fill in all required fields and upload an image");
            return;
        }

        setUploading(true);
        try {
            // 1. Upload image
            const fileExt = imageFile.name.split('.').pop();
            const fileName = `${Date.now()}.${fileExt}`;
            const filePath = `borders/${fileName}`;

            // Check if bucket exists, if not use 'public' or whatever is available, 
            // but for now I assume 'shop_assets' or similar might not exist so let's stick to a standard one or create it.
            // Actually, best to use a bucket named 'shop-assets' if possible.
            // Let's assume 'public' bucket exists for now as a fallback or 'shop_items'.
            // Checking common buckets: 'avatars', 'game_images'. Let's use 'game_images' as a safe bet or 'public'.
            // Actually, I'll try 'shop_items' bucket first, effectively.

            // NOTE: We need to make sure the bucket exists. 
            // For now I will try to upload to 'shop-assets'. If it fails, I might need to create it or use another one.
            const bucketName = 'shop-assets';

            const { error: uploadError } = await supabase.storage
                .from(bucketName)
                .upload(filePath, imageFile);

            if (uploadError) {
                // Determine if error is "Bucket not found"
                if (uploadError.message.includes("Bucket not found") || uploadError.message.includes("not found")) {
                    // Fallback to 'public' if 'shop-assets' fails
                    const { error: fallbackError } = await supabase.storage
                        .from('public')
                        .upload(`shop/borders/${fileName}`, imageFile);

                    if (fallbackError) throw fallbackError;

                    // If fallback worked, update URL path
                    const { data: { publicUrl } } = supabase.storage
                        .from('public')
                        .getPublicUrl(`shop/borders/${fileName}`);

                    await saveItemToDb(publicUrl);
                } else {
                    throw uploadError;
                }
            } else {
                const { data: { publicUrl } } = supabase.storage
                    .from(bucketName)
                    .getPublicUrl(filePath);

                await saveItemToDb(publicUrl);
            }

        } catch (error: any) {
            console.error('Error adding item:', error);
            toast.error(error.message || "Failed to add item");
        } finally {
            setUploading(false);
        }
    };

    const saveItemToDb = async (imageUrl: string) => {
        const { error } = await supabase
            .from('shop_items')
            .insert({
                name,
                description,
                price_eur: parseFloat(price),
                type: itemType,
                is_active: true,
                is_leveling: isLeveling,
                metadata: {
                    style: imageUrl,
                    style_slug: styleSlug,
                    duration_days: itemType === 'boost' ? parseInt(styleSlug) : undefined
                }
            });

        if (error) throw error;

        toast.success("Product added successfully");
        setName("");
        setDescription("");
        setPrice("");
        setImageFile(null);
        setPreviewUrl(null);
        fetchItems();
    };

    const handleEdit = (item: ShopItem) => {
        setEditingItem(item);
        setName(item.name);
        setDescription(item.description || "");
        setPrice(item.price_eur.toString());
        setPreviewUrl(item.metadata?.style || null);
        setIsLeveling(item.is_leveling || false);
        setStyleSlug(item.metadata?.style_slug || "");
        setItemType(item.type);
        setImageFile(null);
    };

    const handleCancelEdit = () => {
        setEditingItem(null);
        setName("");
        setDescription("");
        setPrice("");
        setImageFile(null);
        setPreviewUrl(null);
        setIsLeveling(false);
        setStyleSlug("");
    };

    const handleUpdateItem = async () => {
        if (!editingItem || !name || price === "") {
            toast.error("Please fill in all required fields");
            return;
        }

        setUploading(true);
        try {
            let imageUrl = editingItem.metadata?.style;

            // Upload new image if one was selected
            if (imageFile) {
                const fileExt = imageFile.name.split('.').pop();
                const fileName = `${Date.now()}.${fileExt}`;
                const filePath = `borders/${fileName}`;
                const bucketName = 'shop-assets';

                const { error: uploadError } = await supabase.storage
                    .from(bucketName)
                    .upload(filePath, imageFile);

                if (uploadError) throw uploadError;

                const { data: { publicUrl } } = supabase.storage
                    .from(bucketName)
                    .getPublicUrl(filePath);

                imageUrl = publicUrl;
            }

            // Update item in database
            const { error } = await supabase
                .from('shop_items')
                .update({
                    name,
                    description,
                    price_eur: parseFloat(price),
                    type: itemType,
                    is_leveling: isLeveling,
                    metadata: {
                        style: imageUrl,
                        style_slug: styleSlug,
                        duration_days: itemType === 'boost' ? parseInt(styleSlug) : undefined
                    }
                })
                .eq('id', editingItem.id);

            if (error) throw error;

            toast.success("Product updated successfully");
            handleCancelEdit();
            fetchItems();
        } catch (error: any) {
            console.error('Error updating item:', error);
            toast.error(error.message || "Failed to update item");
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (!confirm("Are you sure you want to delete this item?")) return;

        try {
            const { error } = await supabase
                .from('shop_items')
                .delete()
                .eq('id', id);

            if (error) throw error;

            toast.success("Item deleted");
            fetchItems();
        } catch (error) {
            console.error('Error deleting:', error);
            toast.error("Failed to delete item");
        }
    };

    const handleToggleActive = async (id: string, currentStatus: boolean) => {
        try {
            const { error } = await supabase
                .from('shop_items')
                .update({ is_active: !currentStatus })
                .eq('id', id);

            if (error) throw error;

            toast.success(`Product ${!currentStatus ? 'activated' : 'deactivated'}`);
            fetchItems();
        } catch (error: any) {
            console.error('Error toggling status:', error);
            toast.error(`Failed to update status: ${error.message || error.details || 'Unknown error'}`);
        }
    };

    return (
        <AdminLayout>
            <div className="container mx-auto">
                <div className="flex items-center gap-3 mb-8">
                    <Package className="w-8 h-8 text-primary" />
                    <h1 className="text-3xl font-bold">Manage Products</h1>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Add New Product Form */}
                    <Card className="lg:col-span-1 h-fit">
                        <CardHeader>
                            <CardTitle>{editingItem ? "Edit Product" : "Add New Product"}</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="space-y-2">
                                    <label className="text-sm font-medium">Type</label>
                                    <Select value={itemType} onValueChange={(v: any) => setItemType(v)}>
                                        <SelectTrigger className="w-full">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="border">Border</SelectItem>
                                            <SelectItem value="boost">Boost</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-sm font-medium">Preview</label>
                                    <div className="flex justify-center p-4 bg-slate-900/50 rounded-lg border border-dashed border-slate-700">
                                        <UserAvatar
                                            src={user?.user_metadata?.avatar_url}
                                            fallback={user?.email?.charAt(0).toUpperCase()}
                                            borderStyle={previewUrl || undefined}
                                            size="xl"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-sm font-medium">
                                        Border Image (PNG/GIF) {editingItem && <span className="text-muted-foreground">(optional)</span>}
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <Input
                                            type="file"
                                            accept="image/png,image/gif,image/svg+xml"
                                            onChange={handleImageChange}
                                            className="cursor-pointer"
                                        />
                                    </div>
                                    {editingItem && !imageFile && (
                                        <p className="text-xs text-muted-foreground">Leave empty to keep current image</p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <label className="text-sm font-medium">Name</label>
                                    <Input
                                        placeholder="e.g. Neon Blue"
                                        value={name}
                                        onChange={e => setName(e.target.value)}
                                        required
                                    />
                                </div>

                                <div className="space-y-2">
                                    <label className="text-sm font-medium">
                                        {itemType === 'border' ? "Style Slug (Bronze, Silver, Gold...)" : "Duration (Days)"}
                                    </label>
                                    <Input
                                        placeholder={itemType === 'border' ? "bronze_level" : "1"}
                                        value={styleSlug}
                                        onChange={e => setStyleSlug(e.target.value)}
                                        required
                                    />
                                    <p className="text-[10px] text-muted-foreground italic">
                                        Used for reward matching (must match leveling.ts)
                                    </p>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-sm font-medium">Price (EUR)</label>
                                    <Input
                                        type="number"
                                        step="0.01"
                                        placeholder="0.00"
                                        value={price}
                                        onChange={e => setPrice(e.target.value)}
                                        required
                                    />
                                </div>

                                <div className="space-y-2">
                                    <label className="text-sm font-medium">Description</label>
                                    <Textarea
                                        placeholder="Product description..."
                                        value={description}
                                        onChange={e => setDescription(e.target.value)}
                                    />
                                </div>

                                <div className="flex items-center justify-between p-3 bg-slate-900/30 rounded-lg border border-white/5">
                                    <div className="space-y-0.5">
                                        <label className="text-sm font-medium">Leveling Exclusive</label>
                                        <p className="text-xs text-muted-foreground">Hide from shop, use as reward</p>
                                    </div>
                                    <Switch
                                        checked={isLeveling}
                                        onCheckedChange={setIsLeveling}
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Button type="submit" className="w-full" disabled={uploading}>
                                        {uploading ? (
                                            <>
                                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                {editingItem ? "Updating..." : "Adding..."}
                                            </>
                                        ) : (
                                            <>
                                                {editingItem ? (
                                                    <>
                                                        <Edit className="w-4 h-4 mr-2" />
                                                        Update Product
                                                    </>
                                                ) : (
                                                    <>
                                                        <Plus className="w-4 h-4 mr-2" />
                                                        Add Product
                                                    </>
                                                )}
                                            </>
                                        )}
                                    </Button>
                                    {editingItem && (
                                        <Button type="button" variant="outline" className="w-full" onClick={handleCancelEdit}>
                                            Cancel
                                        </Button>
                                    )}
                                </div>
                            </form>
                        </CardContent>
                    </Card>

                    {/* Products List */}
                    <Card className="lg:col-span-2">
                        <CardHeader className="flex flex-row items-center justify-between space-y-0">
                            <CardTitle>Existing Products ({items.length})</CardTitle>
                            <div className="flex items-center gap-2">
                                <Filter className="w-4 h-4 text-muted-foreground" />
                                <Select value={filter} onValueChange={(v: any) => setFilter(v)}>
                                    <SelectTrigger className="w-[180px] h-9 glass-card border-white/5">
                                        <SelectValue placeholder="Filter items" />
                                    </SelectTrigger>
                                    <SelectContent className="glass-card border-none">
                                        <SelectItem value="all">All Items</SelectItem>
                                        <SelectItem value="borders">All Borders</SelectItem>
                                        <SelectItem value="boosts">All Boosts</SelectItem>
                                        <SelectItem value="leveling">Leveling Only</SelectItem>
                                        <SelectItem value="not_leveling">Shop Only</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </CardHeader>
                        <CardContent>
                            {loading ? (
                                <div className="flex justify-center p-8">
                                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                                </div>
                            ) : items.length === 0 ? (
                                <p className="text-center text-muted-foreground py-8">No products found.</p>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {items
                                        .filter(item => {
                                            if (filter === 'borders') return item.type === 'border';
                                            if (filter === 'boosts') return item.type === 'boost';
                                            if (filter === 'leveling') return item.is_leveling;
                                            if (filter === 'not_leveling') return !item.is_leveling;
                                            return true;
                                        })
                                        .map((item) => (
                                            <div key={item.id} className="group relative flex items-start gap-4 p-4 rounded-xl bg-card/40 backdrop-blur-sm border border-white/5 hover:border-primary/50 transition-all duration-300 overflow-hidden">
                                                {/* Hover Glow Effect */}
                                                <div className="absolute inset-0 bg-gradient-to-r from-primary/0 via-primary/5 to-primary/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

                                                {/* Avatar or Icon */}
                                                <div className="shrink-0 z-10 bg-gradient-to-br from-slate-800 to-slate-900 p-2.5 rounded-xl shadow-inner border border-white/5 min-w-[80px] flex items-center justify-center">
                                                    {item.type === 'border' ? (
                                                        <UserAvatar
                                                            fallback="?"
                                                            borderStyle={item.metadata?.style}
                                                            size="lg"
                                                        />
                                                    ) : (
                                                        <Zap className="w-8 h-8 text-yellow-500" />
                                                    )}
                                                </div>

                                                {/* Content */}
                                                <div className="flex-1 min-w-0 z-10 flex flex-col gap-1.5">
                                                    <div className="flex items-center gap-2">
                                                        <Badge variant="outline" className="text-[9px] h-4 uppercase">{item.type}</Badge>
                                                        <h3 className="font-bold text-lg leading-none truncate">{item.name}</h3>
                                                        <div
                                                            className={`shrink-0 w-2 h-2 rounded-full shadow-[0_0_8px_currentColor] transition-colors duration-300 ${item.is_active ? "bg-green-500 text-green-500" : "bg-slate-600 text-slate-600"}`}
                                                            title={item.is_active ? "Active" : "Inactive"}
                                                        />
                                                    </div>

                                                    <p className="text-sm text-muted-foreground line-clamp-1">{item.description}</p>

                                                    <div className="flex items-baseline gap-2">
                                                        <span className="text-base font-bold text-primary">{item.price_eur}</span>
                                                        <span className="text-xs font-medium text-primary/70">€</span>
                                                        {item.is_leveling && (
                                                            <Badge variant="secondary" className="text-[9px] h-4 bg-purple-500/20 text-purple-400 border-none ml-2">LEVELING</Badge>
                                                        )}
                                                    </div>

                                                    {/* Actions moved below price */}
                                                    <div className="flex items-center justify-between mt-2 pt-3 border-t border-white/5 w-full">
                                                        <div className="flex items-center gap-2">
                                                            <Switch
                                                                checked={item.is_active}
                                                                onCheckedChange={() => handleToggleActive(item.id, item.is_active)}
                                                                className="scale-75 origin-left data-[state=checked]:bg-green-500"
                                                            />
                                                            <span className={`text-[10px] uppercase tracking-widest font-bold transition-colors ${item.is_active ? "text-green-400" : "text-muted-foreground"}`}>
                                                                {item.is_active ? "Active" : "Inactive"}
                                                            </span>
                                                        </div>

                                                        <div className="flex gap-1">
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                onClick={() => handleEdit(item)}
                                                                className="h-8 w-8 text-muted-foreground hover:text-blue-400 hover:bg-blue-400/10 transition-colors"
                                                            >
                                                                <Edit className="w-4 h-4" />
                                                            </Button>
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                onClick={() => handleDelete(item.id)}
                                                                className="h-8 w-8 text-muted-foreground hover:text-red-400 hover:bg-red-400/10 transition-colors"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </AdminLayout>
    );
};

export default AdminProducts;
